import crmEnrichmentFiltersService from "app/pages/sales-intelligence/modules/crm-enrichment/services/filters";
import type {
    CrmEnrichmentJob,
    CrmEnrichmentJobDTO,
    CrmEnrichmentJobFilterType,
    CrmEnrichmentJobObject,
    CrmEnrichmentJobPriceDto,
    CrmEnrichmentJobRefreshPeriod,
    CrmEnrichmentJobType,
    CrmEnrichmentSource,
    EnrichmentJobMappingDTO,
    LeadScoreRefreshDataInterval,
} from "app/pages/sales-intelligence/modules/crm-enrichment/types/types";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import type { CrmColumnItem } from "app/pages/sales-intelligence/sub-modules/management/types";
import CountryService from "app/services/CountryService";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";

type CreateJobDTOParams = {
    readonly jobName: string;
    readonly jobType: CrmEnrichmentJobType | "";
    readonly jobObject: CrmEnrichmentJobObject | "";
    readonly countryId: number | null;
    readonly refreshPeriod?: CrmEnrichmentJobRefreshPeriod | "";
    readonly leadScoreRefreshDataInterval?: LeadScoreRefreshDataInterval | "";
    readonly enrichmentSource: CrmEnrichmentSource;
    readonly isApplyFiltersSelected: boolean | null;
    readonly filters: readonly CrmEnrichmentJobFilterType[];
    readonly columns: readonly CrmColumnItem[];
    readonly initialJob?: CrmEnrichmentJobDTO;
    readonly mappings: readonly EnrichmentJobMappingDTO[];
};

export const createJobDTO = ({
    jobName,
    jobType,
    jobObject,
    countryId,
    refreshPeriod,
    leadScoreRefreshDataInterval,
    enrichmentSource,
    isApplyFiltersSelected,
    filters,
    columns,
    initialJob,
    mappings,
}: CreateJobDTOParams): CrmEnrichmentJobDTO | CrmEnrichmentJob => {
    const refreshDataInterval = jobType === "instant" ? "never" : refreshPeriod || jobType;

    const baseJob = initialJob ?? {};

    return {
        ...baseJob,
        name: jobName,
        enrichmentObject: jobObject as CrmEnrichmentJobObject,
        refreshDataInterval: refreshDataInterval as CrmEnrichmentJobRefreshPeriod,
        leadScoreRefreshDataInterval: (leadScoreRefreshDataInterval as LeadScoreRefreshDataInterval) || undefined,
        type: jobType as CrmEnrichmentJobType,
        country: countryId as number,
        enrichmentSource,
        filters: isApplyFiltersSelected
            ? filters.map((filter) => crmEnrichmentFiltersService.createFilterDTO(filter, columns))
            : [],
        mappings,
    };
};

export const mappingsToPriceFields = (mappings: readonly EnrichmentJobMappingDTO[]): CrmEnrichmentJobPriceDto =>
    mappings
        .filter((m): m is EnrichmentJobMappingDTO & { metadata: string; sourceType: number } =>
            m.metadata != null && m.sourceType != null,
        )
        .map(({ metadata, sourceType }) => ({ metadata, sourceType }));

export const getDefaultCountryId = (): number => {
    const countries = salesSettingsHelper.getAllowedCountries();

    return countries.some(({ id }) => CountryService.isWorldwideCountry(id)) ? WORLDWIDE_COUNTRY_ID : countries[0].id;
};
